import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  Download, 
  Printer, 
  FileSpreadsheet,
  BarChart3,
  RefreshCw,
  Filter
} from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#dc2626", "#ef4444", "#f87171", "#fca5a5", "#b91c1c", "#991b1b"];

// Platform options for filtering - simplified
const PLATFORM_OPTIONS = [
  { value: "all", label: "All Platforms", color: "bg-gray-100 text-gray-700" },
  // FB/J&T by branch (main platform - QC and PQ only)
  { value: "FB/J&T-QC", label: "FB/J&T-QC", color: "bg-blue-100 text-blue-700" },
  { value: "FB/J&T-PQ", label: "FB/J&T-PQ", color: "bg-blue-100 text-blue-700" },
  // Other platforms
  { value: "Shopee", label: "Shopee", color: "bg-orange-100 text-orange-700" },
  { value: "TikTok", label: "TikTok", color: "bg-gray-700 text-white" },
  { value: "Lazada", label: "Lazada", color: "bg-purple-100 text-purple-700" },
];

// Platform colors for charts
const PLATFORM_COLORS = {
  "FB/J&T": "#3b82f6",
  "Facebook/J&T": "#3b82f6",
  "Shopee": "#f97316", 
  "TikTok": "#111827",
  "Lazada": "#8b5cf6",
  "Unknown": "#6b7280"
};

export default function Reports() {
  const [period, setPeriod] = useState("daily");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("period", period);
      if (startDate) params.append("start_date", format(startDate, "yyyy-MM-dd"));
      if (endDate) params.append("end_date", format(endDate, "yyyy-MM-dd"));
      if (platformFilter && platformFilter !== "all") params.append("platform", platformFilter);

      const response = await axios.get(`${API}/rts/reports/data?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [period, platformFilter]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", format(startDate, "yyyy-MM-dd"));
      if (endDate) params.append("end_date", format(endDate, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/rts/reports/export?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rts_report_${format(new Date(), "yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report exported successfully!");
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Page Header */}
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 mt-1">Generate at i-export ang RTS reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Report Controls */}
      <Card className="no-print">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            {/* Period Tabs */}
            <div className="flex-1">
              <Label className="mb-2 block">Report Period</Label>
              <Tabs value={period} onValueChange={setPeriod}>
                <TabsList data-testid="period-tabs">
                  <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
                  <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
                  <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Platform Filter */}
            <div>
              <Label className="mb-2 block flex items-center gap-1">
                <Filter className="w-3 h-3" /> Platform
              </Label>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger className="w-[160px]" data-testid="platform-filter">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div>
              <Label className="mb-2 block">Start Date</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[180px] justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                    data-testid="report-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="mb-2 block">End Date</Label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[180px] justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                    data-testid="report-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setEndDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button onClick={fetchReport} className="bg-red-600 hover:bg-red-700" data-testid="generate-report-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="report-loading">
          <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
        </div>
      ) : reportData ? (
        <div className="space-y-6 print-container">
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Report Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-500">Period</p>
                  <p className="text-lg font-semibold capitalize">{reportData.period}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-500">Date Range</p>
                  <p className="text-lg font-semibold">
                    {reportData.start_date} to {reportData.end_date}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-600">Total RTS</p>
                  <p className="text-3xl font-bold text-red-600">{reportData.total_count}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-600">Total Entries</p>
                  <p className="text-3xl font-bold text-red-600">{reportData.entries?.length || 0}</p>
                </div>
              </div>

              {/* Platform Breakdown Section */}
              {reportData.breakdown?.platforms_by_branch && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                    <Filter className="w-4 h-4" /> RTS by Platform & Branch
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {Object.entries(reportData.breakdown.platforms_by_branch)
                      .sort((a, b) => b[1] - a[1])
                      .map(([platformBranch, count]) => {
                      const total = reportData.total_count || 1;
                      const percent = Math.round((count / total) * 100);
                      // Determine color based on platform part
                      const basePlatform = platformBranch.split('-')[0];
                      const color = basePlatform === "Shopee" ? "#f97316" :
                                   basePlatform === "TikTok" ? "#111827" :
                                   basePlatform === "Lazada" ? "#8b5cf6" :
                                   "#3b82f6"; // FB/J&T default
                      return (
                        <div 
                          key={platformBranch}
                          className="p-3 rounded-lg border-2 cursor-pointer hover:shadow-md transition-shadow"
                          style={{ borderColor: color, backgroundColor: `${color}10` }}
                        >
                          <div className="text-xs font-bold" style={{ color }}>{platformBranch}</div>
                          <div className="text-2xl font-bold" style={{ color }}>{count}</div>
                          <div className="text-xs text-slate-500">{percent}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 no-print">
            {/* Items Breakdown Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Items Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reportData.breakdown?.items?.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reportData.breakdown.items.slice(0, 5)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#dc2626" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reasons Breakdown - Changed to List instead of Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Reasons Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {reportData.breakdown?.reasons?.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {reportData.breakdown.reasons.slice(0, 10).map((reason, index) => {
                      const total = reportData.breakdown.reasons.reduce((sum, r) => sum + r.count, 0);
                      const percent = total > 0 ? Math.round((reason.count / total) * 100) : 0;
                      return (
                        <div 
                          key={index} 
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                        >
                          <div 
                            className="w-3 h-3 rounded-sm shrink-0" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate" title={reason.name}>
                              {reason.name.length > 40 ? reason.name.substring(0, 40) + "..." : reason.name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-gray-500">{percent}%</span>
                            <Badge variant="secondary">{reason.count}</Badge>
                          </div>
                        </div>
                      );
                    })}
                    {reportData.breakdown.reasons.length > 10 && (
                      <p className="text-xs text-gray-400 text-center py-1">
                        +{reportData.breakdown.reasons.length - 10} more reasons
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Breakdown Tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Items Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Items</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.breakdown?.items?.slice(0, 5).map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{item.count}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!reportData.breakdown?.items || reportData.breakdown.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-400">
                          No data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Staff Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Staff (Ordered By)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff</TableHead>
                      <TableHead className="text-right">RTS Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.breakdown?.staff?.slice(0, 5).map((staff, i) => (
                      <TableRow key={i}>
                        <TableCell>{staff.name}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{staff.count}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!reportData.breakdown?.staff || reportData.breakdown.staff.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-400">
                          No data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Upsellers Table - NEW */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Upsellers</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Upseller</TableHead>
                      <TableHead className="text-right">RTS Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.breakdown?.upsellers?.slice(0, 5).map((upseller, i) => (
                      <TableRow key={i}>
                        <TableCell>{upseller.name}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{upseller.count}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!reportData.breakdown?.upsellers || reportData.breakdown.upsellers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-400">
                          No upseller data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Reasons Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Reasons</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.breakdown?.reasons?.slice(0, 5).map((reason, i) => (
                      <TableRow key={i}>
                        <TableCell>{reason.name}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{reason.count}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!reportData.breakdown?.reasons || reportData.breakdown.reasons.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-400">
                          No data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Records Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Records</CardTitle>
            </CardHeader>
            <CardContent>
              {reportData.entries?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table data-testid="report-records-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tracking #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Staff</TableHead>
                        <TableHead>Upseller</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-mono text-sm">
                            {entry.tracking_number}
                          </TableCell>
                          <TableCell>{entry.date_ordered}</TableCell>
                          <TableCell>{entry.customer_name}</TableCell>
                          <TableCell>{entry.ordered_by}</TableCell>
                          <TableCell>
                            {entry.upselled_by ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                {entry.upselled_by}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {entry.orders?.map((o) => `${o.item_name} x${o.quantity}`).join(", ")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{entry.reason}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  No records found for this period
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
